import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React from 'react';
import PropTypes from 'prop-types';
import { CollabConnectionContext } from './CollabConnectionProvider';
import { window } from '../helpers/BrowserHelpers';
import { DOC_STATUS_INIT, DOC_STATUS_SYNCED, DOC_STATUS_ERROR } from '../types/DocStatusTypes';
import uuid from 'uuid';
export var CollabDocumentContext = React.createContext({});
export default function CollabDocumentProvider(_ref) {
  var id = _ref.id,
      collection = _ref.collection,
      type = _ref.type,
      children = _ref.children;
  var doc = useDocSubscriber({
    collection: collection,
    id: id,
    type: type
  });

  var _useDocStatus = useDocStatus(doc),
      docStatus = _useDocStatus.docStatus,
      onError = _useDocStatus.onError;

  return React.createElement(CollabDocumentContext.Provider, {
    value: {
      doc: doc,
      docStatus: docStatus,
      onError: onError
    }
  }, children);
}
CollabDocumentProvider.propTypes = {
  id: PropTypes.string,
  collection: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.any
};

function useDocSubscriber(_ref2) {
  var collection = _ref2.collection,
      id = _ref2.id,
      type = _ref2.type;

  var _React$useContext = React.useContext(CollabConnectionContext),
      connection = _React$useContext.connection;

  var doc = React.useMemo(function () {
    var doc = connection.get(collection, id); // Document can be mutated from note to database, this detects the change
    // and invalidate local copy.

    if (type && doc.type && doc.type.name !== type) {
      connection._destroyDoc(doc);

      doc.__sliteTypeChanged = true;
      return connection.get(collection, id);
    }

    return doc;
  }, [connection, collection, id, type]);
  React.useEffect(function () {
    var docConnectionOwner = uuid();
    doc.__sliteDocConnectionOwner = docConnectionOwner;

    if (!doc.subscribed) {
      doc.subscribe(function (error) {
        return error && console.error(error);
      });
    }

    return function () {
      if (doc.__sliteTypeChanged === true) {
        delete doc.__sliteTypeChanged;
        return;
      } // Copied from doc.destroy(). We want the internal behavior + a check on
      // the owner of the doc.


      doc.whenNothingPending(function () {
        if (doc.__sliteDocConnectionOwner !== docConnectionOwner) return;

        if (doc.wantSubscribe) {
          doc.unsubscribe(function (err) {
            if (err) {
              return doc.emit('error', err);
            }

            doc.connection._destroyDoc(doc);
          });
        } else {
          doc.connection._destroyDoc(doc);
        }
      });
    };
  }, [doc]);
  return doc;
}

function useDocStatus(doc) {
  var _React$useState = React.useState(DOC_STATUS_INIT),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      docStatus = _React$useState2[0],
      setDocStatus = _React$useState2[1];

  React.useEffect(function () {
    setDocStatus(DOC_STATUS_SYNCED);

    var onDocumentError = function onDocumentError(error) {
      setDocStatus(DOC_STATUS_ERROR);
      console.error(error);

      if (window.bugsnag && window.bugsnag.notify) {
        window.bugsnag.notify(error);
      }
    };

    doc.on('error', onDocumentError);
    return function () {
      return doc.removeListener('error', onDocumentError);
    };
  }, [doc]);
  var onError = React.useCallback(function (error) {
    if (doc) {
      doc.emit('error', error);
    }
  }, [doc]);
  return {
    docStatus: docStatus,
    onError: onError
  };
}